import { ReportData } from "@/inspection/shared/dtos/ReportData";
import { InspectionRepoContainer } from "@/inspection/shared/repositories/InspectionRepoContainer";
import { Dictionary } from "@/shared/datastructures/Dictionary";
import { ServerResponse } from "@/shared/datastructures/Response";
import { StepperStep } from "@/shared/form/FormTypes";
import { RequestModel } from "@/shared/model/RequestModel";
import { RequestHandler } from "@/shared/utils/RequestHandler";
import { UserSettings } from "@/storage/UserSettings";
import { DateUtils } from "@/utils/DateUtils";
import { ReportDataForm } from "../forms/ReportDataForm";
import { DOCUMENT_LIST_FIELD } from "../forms/BauwerksbuchDocumentListForm";
import {
  AnyForm,
  ReportFormatDefinition,
  reportFormatDefinition
} from "./ReportFormatDefinitions";

export class ReportDataModel extends RequestModel<ReportData> {
  public currentStep = 1;
  public reportUrl = "";

  public saveResponse = new ServerResponse<string>();

  private form: ReportDataForm;
  private definition?: ReportFormatDefinition;
  private subFormsByKey: Dictionary<AnyForm> = {};
  private wiredFormat?: string;
  private wiring: Promise<unknown> = Promise.resolve();

  private assignmentId = 0;
  private reportType = "";
  private reportId = 0;

  public constructor() {
    super();
    this.form = new ReportDataForm();
    this.form.init();
  }

  public load(assignmentId: string, reportType: string, reportId: string) {
    this.assignmentId = parseInt(assignmentId, 10);
    this.reportType = reportType;
    this.reportId = parseInt(reportId, 10);

    const wiring = this.wireSubForms();

    RequestHandler.handleModel(
      async () => {
        // the sub-forms create their fields asynchronously; awaiting that here
        // guarantees that from() writes into the finished fields
        await wiring;

        return InspectionRepoContainer.reportRepo.getReportData(
          UserSettings.getNumericUserId(),
          assignmentId,
          reportType
        );
      },
      this,
      undefined,
      {
        onSuccess: data => (this.reportUrl = data.url)
      }
    );
  }

  public save() {
    RequestHandler.handleFormUpdate(
      () => {
        const data = JSON.stringify(this.form.getData());
        return InspectionRepoContainer.reportRepo.updateReportData(
          data,
          this.assignmentId,
          this.reportType
        );
      },
      {
        form: this.form
      },
      this.saveResponse,
      {
        onSuccess: (url: string) => (this.reportUrl = url),
        onLoad: () => (this.reportUrl = "")
      }
    );
  }

  public get steps(): StepperStep[] {
    return this.definition?.steps ?? [];
  }

  public get isFirstStep() {
    return this.currentStep === 1;
  }

  public get nextButtonText() {
    return this.currentStep >= this.steps.length
      ? "Speichern & Erstellen"
      : "Weiter";
  }

  public get currentFormValid() {
    const subForms = this.definition?.subForms ?? [];
    const currentSubForm = subForms[this.currentStep - 1];

    if (!currentSubForm) {
      return false;
    }

    if (this.currentStep === subForms.length) {
      return this.completeFormValid;
    }

    return this.subFormsByKey[currentSubForm.key]?.isValid() ?? false;
  }

  public get completeFormValid() {
    return this.form.isValid();
  }

  public getGeneralField(fieldName: string) {
    return this.getSubFormField("general", fieldName);
  }

  public setGeneralFieldValue(fieldName: string, value: unknown) {
    return this.setSubFormFieldValue("general", fieldName, value);
  }

  public getImprintField(fieldName: string) {
    return this.getSubFormField("imprint", fieldName);
  }

  public setImprintFieldValue(fieldName: string, value: unknown) {
    return this.setSubFormFieldValue("imprint", fieldName, value);
  }

  public getTaskField(fieldName: string) {
    return this.getSubFormField("task", fieldName);
  }

  public setTaskFieldValue(fieldName: string, value: unknown) {
    return this.setSubFormFieldValue("task", fieldName, value);
  }

  public getOtherField(fieldName: string) {
    return this.getSubFormField("other", fieldName);
  }

  public setOtherFieldValue(fieldName: string, value: unknown) {
    return this.setSubFormFieldValue("other", fieldName, value);
  }

  public getBuildingField(fieldName: string) {
    return this.getSubFormField("building", fieldName);
  }

  public setBuildingFieldValue(fieldName: string, value: unknown) {
    return this.setSubFormFieldValue("building", fieldName, value);
  }

  public getBookInfoField(fieldName: string) {
    return this.getSubFormField("bookInfo", fieldName);
  }

  public setBookInfoFieldValue(fieldName: string, value: unknown) {
    return this.setSubFormFieldValue("bookInfo", fieldName, value);
  }

  /**
   * The document list is one value, not a set of named fields: its sub-form
   * keeps the whole option list in a single field and serializes it as the
   * bare `content.documentList` array.
   */
  public getDocumentListField() {
    return this.getSubFormField("documentList", DOCUMENT_LIST_FIELD);
  }

  public setDocumentList(value: unknown) {
    return this.setSubFormFieldValue("documentList", DOCUMENT_LIST_FIELD, value);
  }

  public getWaterField(fieldName: string) {
    return this.getSubFormField("general", fieldName);
  }

  public setWaterFieldValue(fieldName: string, value: unknown) {
    return this.setSubFormFieldValue("general", fieldName, value);
  }

  public nextStep() {
    if (this.currentStep < this.steps.length) {
      this.currentStep++;
    } else {
      this.save();
    }
  }

  public prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  public jumpToStep(step: number) {
    if (this.currentFormValid) {
      this.currentStep = step;
    }
  }

  public from(data: ReportData): void {
    const reportData = data.data;
    this.reportUrl = data.url;

    for (const subFormKey of Object.keys(reportData)) {
      const subData = reportData[subFormKey] as Dictionary<unknown>;
      const subForm = this.form.getSubFormByIndex(subFormKey);

      for (const fieldName of Object.keys(subData)) {
        const value = subData[fieldName];
        subForm.setFieldValue(fieldName, value);
      }
    }

    const imprint = reportData.imprint as Dictionary<unknown> | undefined;

    if (imprint && imprint.editorialDeadline) {
      this.setImprintFieldValue(
        "editorialDeadline",
        DateUtils.value(imprint.editorialDeadline as string)
      );
    }
  }

  private wireSubForms(): Promise<unknown> {
    if (this.wiredFormat === this.reportType) {
      return this.wiring;
    }

    this.definition = reportFormatDefinition(this.reportType);
    this.subFormsByKey = {};

    const initializations: Array<Promise<void>> = [];

    for (const subForm of this.definition?.subForms ?? []) {
      const form = subForm.createForm();
      initializations.push(form.init());

      this.subFormsByKey[subForm.key] = form;
      this.form.addSubForm(form, subForm.key, true);
    }

    this.wiredFormat = this.reportType;
    this.wiring = Promise.all(initializations);

    return this.wiring;
  }

  private getSubFormField(subFormKey: string, fieldName: string) {
    return this.subFormsByKey[subFormKey]?.getField(fieldName);
  }

  private setSubFormFieldValue(
    subFormKey: string,
    fieldName: string,
    value: unknown
  ) {
    return this.subFormsByKey[subFormKey]?.setFieldValue(fieldName, value);
  }
}
